
import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Divider } from 'antd';
import { ArrowUpOutlined, WarningOutlined } from '@ant-design/icons';
import { AnalysisResult, AnalysisStatus } from '../../../types';

const { Title, Text } = Typography;

interface FlagsAndHighlightsProps {
  results: AnalysisResult;
  status: AnalysisStatus;
}

const FlagsAndHighlights: React.FC<FlagsAndHighlightsProps> = ({ results, status }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { flags_and_highlights } = results;

  if (status === 'error') {
    return <div style={{ padding: '24px' }}><Text>There was an unexpected error with flags and highlights. Once the report is complete, please rerun the analysis.</Text></div>;
  } else if (status === 'skipped') {
    return <div style={{ padding: '24px' }}><Text>Flags and highlights were skipped by the user.</Text></div>;
  }
  if (!flags_and_highlights) {
    return <div style={{ padding: '24px' }}><Text>No flags and highlights data available.</Text></div>;
  }

  const topFlags = (flags_and_highlights.flags || [])
    .sort((a: any, b: any) => (b.concern_level || 0) - (a.concern_level || 0))
    .slice(0, 3);

  const topHighlights = (flags_and_highlights.highlights || []).slice(0, 3);

  const highlightsContent = (
    <div style={{ padding: '16px' }}>
      <Title level={5} style={{ textAlign: 'center', marginBottom: '16px' }}>
        Highlights <ArrowUpOutlined style={{ fontSize: '1em', verticalAlign: 'middle' }} />
      </Title>
      {topHighlights.length > 0 ? (
        <ul>
          {topHighlights.map((highlight: any, index: number) => (
            <li key={index} style={{ marginBottom: '8px' }}>
              <Text>
                {typeof highlight === 'string'
                  ? highlight
                  : highlight.highlight || highlight.text || highlight.description || JSON.stringify(highlight)}
              </Text>
            </li>
          ))}
        </ul>
      ) : (
        <Text type="secondary">No highlights identified.</Text>
      )}
    </div>
  );

  const concernsContent = (
    <div style={{ padding: '16px' }}>
      <Title level={5} style={{ textAlign: 'center', marginBottom: '16px' }}>
        Concerns <WarningOutlined style={{ fontSize: '1em', verticalAlign: 'middle' }} />
      </Title>
      {topFlags.length > 0 ? (
        <ul>
          {topFlags.map((flag: any, index: number) => (
            <li key={index} style={{ marginBottom: '12px' }}>
              <Text>
                {typeof flag.description === 'string'
                  ? flag.description
                  : flag.text || flag.issue || JSON.stringify(flag.description)}
              </Text>
            </li>
          ))}
        </ul>
      ) : (
        <Text type="secondary">No flags identified.</Text>
      )}
    </div>
  );

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
      {isMobile ? (
        <>
          <div style={{ backgroundColor: '#f6ffed' }}>
            {highlightsContent}
          </div>
          <Divider style={{ margin: 0 }} />
          <div style={{ backgroundColor: '#fff1f0' }}>
            {concernsContent}
          </div>
        </>
      ) : (
        <Row wrap={false} align="stretch">
          <Col span={12} style={{ backgroundColor: '#f6ffed' }}>
            {highlightsContent}
          </Col>
          <Divider type="vertical" style={{ height: 'auto', margin: 0 }} />
          <Col span={12} style={{ backgroundColor: '#fff1f0' }}>
            {concernsContent}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default FlagsAndHighlights;
